import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import {Box, Divider, Stack, Typography} from "@mui/material";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {ReactNode, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import type {DateRange} from "../../common/newDatePicker/datePickerShared";
import InsightsDateRangeControl from "../../insights/v2/base/InsightsDateRangeControl";
import {createDefaultInsightsRange} from "../../insights/v2/insightsDateRangePresets";
import {
    RightSidebarActionButton,
    RightSidebarDivider,
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailHeaderCenter,
    RightSidebarRailLayout,
} from "../../layout/rightSidebar/RightSidebarRail";
import {InsightsRailSearch, matchesTitle} from "../../insights/kit/InsightsRailSearch";
import SurveyInvitationsRailButton from "../invitations/SurveyInvitationsRailButton";
import {ResponsesPerDayCard} from "../insights/cards/ResponsesPerDayCard";
import {StatCard, StatCardData} from "../../insights/kit/StatCard";
import {CARD_GAP, CONTENT_MAX_WIDTH} from "../insights/constants";
import {BySurveyCard} from "../insights/cards/BySurveyCard";
import {toPeriodStatCards, toResponsesSeries, toStayStatCards} from "./overview/overviewCards";
import {useSurveySummary} from "./overview/useSurveySummary";
import SurveyInviteRailButton from "../share/SurveyInviteRailButton";

interface Insight {
    key: string;
    title: string;
    node: ReactNode;
}

type Section =
    | {key: string; kind: "stats"; cards: StatCardData[]}
    | {key: string; kind: "full"; insights: Insight[]};

function SurveysOverviewTab() {
    const {t} = useTranslation();
    const navigate = useNavigate();

    useTrackScreenVisit("surveys overview");

    const [range, setRange] = useState<DateRange>(createDefaultInsightsRange);
    const [compareRange, setCompareRange] = useState<DateRange | null>(null);
    const [query, setQuery] = useState("");
    const [isContentScrolled, setIsContentScrolled] = useState(false);

    const {summary, loading} = useSurveySummary(range, compareRange);

    const sections = useMemo<Section[]>(() => [
        {
            key: "stats",
            kind: "stats",
            cards: [...toStayStatCards(summary, t), ...toPeriodStatCards(summary, t)],
        },
        {
            key: "responses",
            kind: "full",
            insights: [{
                key: "Responses per day",
                title: t("Responses per day"),
                node: <ResponsesPerDayCard series={toResponsesSeries(summary)} loading={loading}/>,
            }],
        },
        {
            key: "surveys",
            kind: "full",
            insights: [{
                key: "By survey",
                title: t("By survey"),
                node: <BySurveyCard surveys={summary?.surveys} loading={loading}/>,
            }],
        },
    ], [loading, summary, t]);

    const visible = useMemo<Section[]>(() => sections.flatMap((section): Section[] => {
        if (section.kind === "stats") {
            const cards = section.cards.filter(card => matchesTitle(query, card.title, card.id));
            return cards.length > 0 ? [{...section, cards}] : [];
        }

        const insights = section.insights.filter(insight => matchesTitle(query, insight.title, insight.key));
        return insights.length > 0 ? [{...section, insights}] : [];
    }), [query, sections]);

    const sectionNode = (section: Section) => {
        switch (section.kind) {
            case "stats":
                // Even rows from lg up: eight cards in fours, six in threes - the kit's auto-filling grid leaves a short one.
                return (
                    <Box
                        sx={{
                            display: "grid",
                            gap: 1.5,
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                lg: `repeat(${section.cards.length % 4 === 0 ? 4 : 3}, minmax(0, 1fr))`,
                            },
                        }}
                    >
                        {section.cards.map(card => <StatCard key={card.id} data={card} loading={loading}/>)}
                    </Box>
                );
            case "full":
                return section.insights.map(insight => <Box key={insight.key}>{insight.node}</Box>);
        }
    };

    return (
        <>
            <RightSidebarRailLayout>
                <RightSidebarRailHeader>
                    <RightSidebarRailHeaderCenter>
                        <InsightsDateRangeControl
                            range={range}
                            compareRange={compareRange}
                            onChange={selection => {
                                setRange(selection.range);
                                setCompareRange(selection.compareRange);
                            }}
                        />
                    </RightSidebarRailHeaderCenter>
                </RightSidebarRailHeader>

                <Divider sx={{borderColor: isContentScrolled ? "divider" : "transparent"}}/>

                <Box
                    onScroll={event => setIsContentScrolled(event.currentTarget.scrollTop > 0)}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                        overflowY: "auto",
                        pt: {xs: 2, md: 3},
                        pb: {xs: 6, md: 9},
                        px: {xs: 2, md: 3},
                    }}
                >
                    <Stack gap={CARD_GAP} sx={{width: "100%", maxWidth: CONTENT_MAX_WIDTH, mx: "auto", minWidth: 0}}>
                        {visible.length > 0 ? (
                            visible.map(section => (
                                <Stack key={section.key} gap={CARD_GAP}>
                                    {sectionNode(section)}
                                </Stack>
                            ))
                        ) : (
                            <Typography variant={"body2"} color={"text.secondary"} sx={{py: 6, textAlign: "center"}}>
                                {t("No insights match your search.")}
                            </Typography>
                        )}
                    </Stack>
                </Box>

                <RightSidebarRail>
                    <InsightsRailSearch query={query} onQueryChange={setQuery}/>
                    <RightSidebarDivider/>
                    <RightSidebarActionButton
                        icon={<ListAltOutlinedIcon/>}
                        label={t("Surveys")}
                        onClick={() => navigate("/system/surveys")}
                    />
                    {/* Same period as the cards, so the list shows what they count. */}
                    <SurveyInvitationsRailButton range={range}/>
                    <SurveyInviteRailButton/>
                </RightSidebarRail>
            </RightSidebarRailLayout>
        </>
    );
}

export default SurveysOverviewTab;
